/**
 * Add-tag popover for list-rule groups (performance tags)
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { ChevronDown, Plus, Search } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

function getTagOptionStyle(tag: string, colors?: Record<string, string>) {
  const hex = colors?.[tag];
  if (!hex) return undefined;
  return {
    backgroundColor: `${hex}26`,
    color: hex,
  };
}

export interface TagAddPopoverProps {
  options: string[];
  tagColors?: Record<string, string>;
  onSelect: (tag: string) => void;
  disabled?: boolean;
}

export const TagAddPopover: FC<TagAddPopoverProps> = ({
  options,
  tagColors,
  onSelect,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase().trim();
    return options.filter((tag) => tag.toLowerCase().includes(q));
  }, [options, search]);

  const handleSelect = (tag: string) => {
    onSelect(tag);
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  if (options.length === 0) return null;

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Add tag"
          disabled={disabled}
          className={cn(
            'flex min-w-[88px] shrink-0 items-center justify-between gap-2 rounded border border-input bg-background px-2.5 text-left text-xs font-medium text-muted-foreground transition-colors',
            'hover:bg-accent/50 hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'h-7',
          )}
        >
          <Plus className="h-3 w-3 shrink-0" />
          <span>Add tag</span>
          <ChevronDown
            className={cn('h-3 w-3 shrink-0 opacity-80', open && 'rotate-180')}
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-[250] min-w-[200px] max-w-[min(300px,70vw)] rounded border border-border bg-popover text-popover-foreground shadow-lg p-0',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="border-b border-border p-2">
            <div className="flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1.5">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                autoFocus
                autoComplete="off"
              />
            </div>
          </div>
          <div
            role="listbox"
            className="max-h-[260px] overflow-y-auto py-1"
          >
            {filteredOptions.length === 0 ? (
              <p className="px-3 py-4 text-center text-sm text-muted-foreground">
                {options.length === 0 ? 'No tags' : 'No matches'}
              </p>
            ) : (
              filteredOptions.map((tag) => {
                const tagStyle = getTagOptionStyle(tag, tagColors);
                return (
                  <button
                    key={tag}
                    type="button"
                    role="option"
                    className={cn(
                      'group relative flex w-full items-center overflow-hidden px-3 py-2 text-left text-sm font-medium transition-colors',
                      !tagStyle && 'hover:bg-accent/60',
                    )}
                    style={tagStyle}
                    onClick={() => handleSelect(tag)}
                  >
                    {tagStyle && (
                      <span
                        className="pointer-events-none absolute inset-0 bg-white/20 opacity-0 transition-opacity duration-150 group-hover:opacity-100 dark:bg-black/20"
                        aria-hidden
                      />
                    )}
                    <span className="relative">{tag}</span>
                  </button>
                );
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
